import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { Calendar, DollarSign, TrendingUp, Users } from "lucide-react";
import React from "react";
import { useOutstandingSummary } from "../../hooks";

// Helper function to safely convert to number
const safeNumber = (value: any): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const OutstandingSummary: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: summaryData } = useOutstandingSummary(Number(remoteUtilityId));
  const currency = useCurrency();
  const summaryCards = [
    {
      title: "Total Outstanding",
      tooltip: 'Total unpaid amount pending from all accounts.',
      value: safeNumber(summaryData?.totalOutstandingBalance),
      displayValue: summaryData?.totalOutstandingBalance
        ? `${currency}${safeNumber(
            summaryData.totalOutstandingBalance
          ).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : `${currency} 0.00`,
      trend: "up" as const,
      icon: <DollarSign className="h-8 w-8 text-green-500" />,
      color: "text-green-600",
    },
    {
      title: "Accounts",
      tooltip: 'Number of consumer accounts with outstanding dues.',
      value: safeNumber(summaryData?.totalBillConsumerCount),
      displayValue: summaryData?.totalBillConsumerCount
        ? safeNumber(summaryData.totalBillConsumerCount).toLocaleString()
        : "0",
      trend: "up" as const,
      icon: <Users className="h-8 w-8 text-blue-500" />,
      color: "text-blue-600",
    },
    {
      title: "Avg Days Outstanding",
      tooltip: 'Average number of days payments remain unpaid.',
      value: safeNumber(summaryData?.averageDaysOutstanding),
      displayValue: summaryData?.averageDaysOutstanding
        ? `${Math.round(safeNumber(summaryData.averageDaysOutstanding))} days`
        : "0 days",
      trend: "up" as const,
      icon: <Calendar className="h-8 w-8 text-orange-500" />,
      color: "text-orange-600",
    },
    {
      title: "Collection Rate",
      tooltip: 'Percentage of billed amounts successfully collected.',
      value: safeNumber(summaryData?.collectionRatePercentage),
      displayValue: summaryData?.collectionRatePercentage
        ? `${safeNumber(summaryData.collectionRatePercentage).toFixed(1)}%`
        : "0.0%",
      trend: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-purple-500" />,
      color: "text-purple-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {summaryCards.map((card, index) => (
        <QuickStatsCard
          key={index}
          title={card.title}
          count={card.value}
          displayValue={card.displayValue}
          trendDirection={card.trend}
          // trendSummary={card.change}
          icon={card.icon}
          tooltip={card.tooltip}
        />
      ))}
    </div>
  );
};
